import { currentZonedNow, type ZonedNowParts } from '@/utils/appTimezone'
import { clockRangeFromScheduleDisplayForDate, formatOrderScheduleLineDisplay } from '@/utils/orderScheduleDisplay'
import { SLOT_INTERVAL_MINUTES, TIME_SLOT_GRID } from '../constants'

export function allGridSlots(): string[] {
  return TIME_SLOT_GRID.flat().filter((s): s is string => Boolean(s))
}

export function normalizeGridSlot(slot: string): string {
  return slot.trim().toUpperCase().replace(/\s/g, '')
}

export function formatSlotLabel(slot: string): string {
  const m = slot.trim().match(/^(\d{1,2}:\d{2})(AM|PM)$/i)
  if (!m?.[1] || !m[2]) return slot
  return `${m[1]} ${m[2].toUpperCase()}`
}

export function clockMatchVariants(hm: string, ap: string): string[] {
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!m?.[1] || !m[2]) return [normalizeGridSlot(`${hm}${ap.toUpperCase()}`)]
  const h = parseInt(m[1], 10)
  const min = m[2]
  const mer = ap.toUpperCase()
  return [
    normalizeGridSlot(`${h}:${min}${mer}`),
    normalizeGridSlot(`${String(h).padStart(2, '0')}:${min}${mer}`),
  ]
}

export function findGridSlotIndexForClock(hm: string, ap: string): number {
  const all = allGridSlots()
  const variants = clockMatchVariants(hm, ap)
  let idx = all.findIndex((s) => variants.includes(normalizeGridSlot(s)))
  if (idx >= 0) return idx
  for (const v of variants) {
    const labeled = formatSlotLabel(v)
    idx = all.findIndex((s) => formatSlotLabel(s) === labeled)
    if (idx >= 0) return idx
  }
  return -1
}

export function findGridSlotIndexForDisplayTime(displayTime: string): number {
  const m = displayTime.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return -1
  return findGridSlotIndexForClock(`${m[1]}:${m[2]}`, m[3])
}

export function parseClockParts(hm: string, ap: string): { h: number; m: number; ap: string } | null {
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!m?.[1] || !m[2]) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h < 1 || h > 12 || min < 0 || min > 59) return null
  return { h, m: min, ap: ap.toUpperCase() }
}

export function clockPartsToMinutes(parts: { h: number; m: number; ap: string }): number {
  let hour = parts.h % 12
  if (parts.ap === 'PM') hour += 12
  return hour * 60 + parts.m
}

export function gridSlotStartMinutes(slot: string): number | null {
  const m = slot.trim().match(/^(\d{1,2}):(\d{2})(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return null
  const parts = parseClockParts(`${m[1]}:${m[2]}`, m[3])
  return parts ? clockPartsToMinutes(parts) : null
}

function civilDayKey(year: number, month: number, day: number): number {
  return year * 10000 + month * 100 + day
}

function dateCivilKey(d: Date): number {
  return civilDayKey(d.getFullYear(), d.getMonth() + 1, d.getDate())
}

/** 所选时区的「今天」零点（本地 Date 的年月日等于该时区公历日）。 */
export function startOfToday(now: ZonedNowParts = currentZonedNow()): Date {
  return new Date(now.year, now.month - 1, now.day)
}

export function isPastCalendarDay(d: Date, now: ZonedNowParts = currentZonedNow()): boolean {
  return dateCivilKey(d) < civilDayKey(now.year, now.month, now.day)
}

export function isPastTimeSlot(slot: string, date: Date, now: ZonedNowParts = currentZonedNow()): boolean {
  const dayKey = dateCivilKey(date)
  const todayKey = civilDayKey(now.year, now.month, now.day)
  if (dayKey < todayKey) return true
  if (dayKey > todayKey) return false
  const slotMinutes = gridSlotStartMinutes(slot)
  if (slotMinutes == null) return false
  return slotMinutes < now.minutes
}

export function pastSlotsForDate(date: Date, now: ZonedNowParts = currentZonedNow()): Set<string> {
  const out = new Set<string>()
  for (const slot of allGridSlots()) {
    if (isPastTimeSlot(slot, date, now)) out.add(slot)
  }
  return out
}

export function findGridSlotIndexForEndClock(hm: string, ap: string): number {
  const parts = parseClockParts(hm, ap)
  if (!parts) return findGridSlotIndexForClock(hm, ap)

  const endMin = clockPartsToMinutes(parts)
  const all = allGridSlots()
  const exact = findGridSlotIndexForClock(hm, ap)
  if (exact >= 0) {
    const startMin = gridSlotStartMinutes(all[exact]!)
    if (startMin != null && endMin === startMin) return exact
  }

  for (let i = 0; i < all.length; i++) {
    const startMin = gridSlotStartMinutes(all[i]!)
    if (startMin == null) continue
    const nextStart =
      i + 1 < all.length ? gridSlotStartMinutes(all[i + 1]!) : startMin + SLOT_INTERVAL_MINUTES
    if (startMin <= endMin && endMin < nextStart!) return i
  }

  let lastIdx = -1
  for (let i = 0; i < all.length; i++) {
    const startMin = gridSlotStartMinutes(all[i]!)
    if (startMin != null && startMin <= endMin) lastIdx = i
  }
  return lastIdx >= 0 ? lastIdx : exact
}

export function slotGridIndex(slot: string): number {
  const m = slot.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (m?.[1] && m[2] && m[3]) return findGridSlotIndexForClock(`${m[1]}:${m[2]}`, m[3])
  const all = allGridSlots()
  const norm = normalizeGridSlot(slot)
  return all.findIndex((s) => normalizeGridSlot(s) === norm)
}

export function sortSlotsByGridOrder(slots: string[]): string[] {
  const idx = (s: string) => {
    const i = slotGridIndex(s)
    return i < 0 ? 9999 : i
  }
  return [...slots].sort((a, b) => idx(a) - idx(b))
}

export function canonicalGridSlot(slot: string): string | null {
  const idx = slotGridIndex(slot)
  if (idx < 0) return null
  return allGridSlots()[idx] ?? null
}

export function normalizeAvailabilitySlotLabel(raw: string): string | null {
  const t = raw.trim()
  if (!t) return null
  const fromGrid = canonicalGridSlot(t)
  if (fromGrid) return fromGrid
  const m24 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (m24?.[1] && m24[2]) {
    let hour = parseInt(m24[1], 10)
    const minute = m24[2]
    const ap = hour >= 12 ? 'PM' : 'AM'
    if (hour > 12) hour -= 12
    if (hour === 0) hour = 12
    return canonicalGridSlot(`${hour}:${minute}${ap}`)
  }
  return null
}

export function startOfWeekSunday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  x.setDate(x.getDate() - x.getDay())
  return x
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function isDateInWeekRange(d: Date, weekAnchorDate: Date): boolean {
  const start = startOfWeekSunday(weekAnchorDate)
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return day >= start && day <= end
}

export function ymdFromDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function slotsInSurroundedGapsBetweenBookings(occupied: Set<string>): Set<string> {
  const gaps = new Set<string>()
  const all = allGridSlots()
  const isOccupied = (slot: string) => {
    const canonical = canonicalGridSlot(slot) ?? slot
    return occupied.has(canonical) || occupied.has(slot)
  }

  let i = 0
  while (i < all.length) {
    while (i < all.length && !isOccupied(all[i]!)) i++
    if (i >= all.length) break
    while (i < all.length && isOccupied(all[i]!)) i++
    const gapStart = i
    while (i < all.length && !isOccupied(all[i]!)) i++
    const gapEnd = i - 1
    if (gapStart > gapEnd || i >= all.length) continue
    for (let g = gapStart; g <= gapEnd; g++) {
      const slot = all[g]
      if (slot) gaps.add(slot)
    }
  }
  return gaps
}

export function occupiedSlotIndicesFromClockRange(
  startHm: string,
  startAp: string,
  endHm: string,
  endAp: string,
): { startIdx: number; endIdx: number } {
  const startParts = parseClockParts(startHm, startAp)
  const endParts = parseClockParts(endHm, endAp)
  if (!startParts || !endParts) return { startIdx: -1, endIdx: -1 }

  const apptStart = clockPartsToMinutes(startParts)
  const apptEnd = clockPartsToMinutes(endParts)
  if (apptEnd <= apptStart) {
    const startIdx = findGridSlotIndexForClock(startHm, startAp)
    return { startIdx, endIdx: startIdx }
  }

  const all = allGridSlots()
  let startIdx = -1
  let endIdx = -1
  for (let i = 0; i < all.length; i++) {
    const slotStart = gridSlotStartMinutes(all[i]!)
    if (slotStart == null) continue
    if (slotStart >= apptStart && slotStart < apptEnd) {
      if (startIdx < 0) startIdx = i
      endIdx = i
    }
  }
  if (startIdx < 0) {
    startIdx = findGridSlotIndexForClock(startHm, startAp)
    endIdx = findGridSlotIndexForEndClock(endHm, endAp)
  }
  return { startIdx, endIdx }
}

export function gridSlotFromDisplayClock(clock: string): string | null {
  const m = clock.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return null
  return canonicalGridSlot(`${m[1]}:${m[2]}${m[3].toUpperCase()}`)
}

export function clockRangeFromBookedTimeLabel(
  timeLabel: string,
  anchorDate?: Date | null,
): { startHm: string; startAp: string; endHm: string; endAp: string } | null {
  const label = timeLabel.trim()
  if (!label || label === '—') return null
  if (anchorDate) {
    const scoped = clockRangeFromScheduleDisplayForDate(label, anchorDate)
    if (scoped) return scoped
  }
  const formatted = formatOrderScheduleLineDisplay(label)
  const text = formatted !== '—' ? formatted : label
  if (anchorDate) {
    const scoped = clockRangeFromScheduleDisplayForDate(text, anchorDate)
    if (scoped) return scoped
  }
  const rangeRe = /(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/gi
  const ranges = [...text.matchAll(rangeRe)]
  const hit = ranges[ranges.length - 1]
  if (!hit?.[1] || !hit[2] || !hit[3] || !hit[4]) return null
  return {
    startHm: hit[1],
    startAp: hit[2].toUpperCase(),
    endHm: hit[3],
    endAp: hit[4].toUpperCase(),
  }
}

export function slotsInClockRangeForDisplay(range: {
  startHm: string
  startAp: string
  endHm: string
  endAp: string
}): string[] {
  const startParts = parseClockParts(range.startHm, range.startAp)
  const endParts = parseClockParts(range.endHm, range.endAp)
  if (!startParts || !endParts) return []

  const apptStart = clockPartsToMinutes(startParts)
  const apptEnd = clockPartsToMinutes(endParts)

  if (apptEnd <= apptStart) {
    const only = findGridSlotIndexForClock(range.startHm, range.startAp)
    return only >= 0 ? [allGridSlots()[only]!] : []
  }

  const picked: string[] = []
  for (const slot of allGridSlots()) {
    const slotStart = gridSlotStartMinutes(slot)
    if (slotStart == null) continue
    if (slotStart >= apptStart && slotStart < apptEnd) picked.push(slot)
  }

  if (!picked.length) {
    const startOnly = findGridSlotIndexForClock(range.startHm, range.startAp)
    if (startOnly >= 0) picked.push(allGridSlots()[startOnly]!)
  }

  // 结束时刻为区间外边界（如 10:30–12:00），不再把结束格点追加为占用起点
  return sortSlotsByGridOrder(picked)
}

export function gridSlotIndicesFromTimeLabel(
  timeLabel: string,
  anchorDate?: Date | null,
): { startIdx: number; endIdx: number } {
  if (anchorDate) {
    const scoped = clockRangeFromScheduleDisplayForDate(timeLabel, anchorDate)
    if (scoped) {
      return occupiedSlotIndicesFromClockRange(
        scoped.startHm,
        scoped.startAp,
        scoped.endHm,
        scoped.endAp,
      )
    }
  }
  const times = [...timeLabel.matchAll(/(\d{1,2}:\d{2})\s*(AM|PM)/gi)]
  if (times.length >= 2 && times[0]?.[1] && times[0]?.[2]) {
    const last = times[times.length - 1]!
    if (last[1] && last[2]) {
      return occupiedSlotIndicesFromClockRange(times[0][1], times[0][2], last[1], last[2])
    }
  }
  let startIdx = -1
  let endIdx = -1
  if (times.length >= 1 && times[0]?.[1] && times[0]?.[2]) {
    startIdx = findGridSlotIndexForClock(times[0][1], times[0][2])
    endIdx = startIdx
  }
  return { startIdx, endIdx }
}

export function minuteRangeFromSelectedSlots(slots: string[]): import('../types').MinuteRange | null {
  const ordered = sortSlotsByGridOrder(slots)
  if (!ordered.length) return null
  const startMin = gridSlotStartMinutes(ordered[0]!)
  const lastMin = gridSlotStartMinutes(ordered[ordered.length - 1]!)
  if (startMin == null || lastMin == null) return null
  return { start: startMin, endExclusive: lastMin + SLOT_INTERVAL_MINUTES }
}

export function applyContiguousSlotSelection(slots: Iterable<string>): Set<string> {
  const ordered = sortSlotsByGridOrder([...slots])
  if (!ordered.length) return new Set()

  const startIdx = slotGridIndex(ordered[0]!)
  const endIdx = slotGridIndex(ordered[ordered.length - 1]!)
  if (startIdx < 0 || endIdx < 0) return new Set(ordered)

  const all = allGridSlots()
  const from = Math.min(startIdx, endIdx)
  const to = Math.max(startIdx, endIdx)
  const filled = new Set<string>()
  for (let i = from; i <= to; i++) {
    const cell = all[i]
    if (cell) filled.add(cell)
  }
  return filled
}

export function gridSlotsStrictlyBetween(startIdx: number, endIdx: number): string[] {
  const all = allGridSlots()
  const from = Math.min(startIdx, endIdx)
  const to = Math.max(startIdx, endIdx)
  const middle: string[] = []
  for (let i = from + 1; i < to; i++) {
    const cell = all[i]
    if (cell) middle.push(cell)
  }
  return middle
}

export function buildOccupiedSlotsFromSelectedSlots(slots: string[]): string[] {
  return sortSlotsByGridOrder(slots)
    .map((s) => canonicalGridSlot(s) ?? s)
    .filter((s) => slotGridIndex(s) >= 0)
}

export function displaySlotsFromBookedTimeLabel(
  timeLabel: string,
  date: Date,
): string[] {
  const range = clockRangeFromBookedTimeLabel(timeLabel, date)
  if (!range) return []
  return slotsInClockRangeForDisplay(range)
}

export function dateFromScheduleDisplay(scheduledAt: string): Date | null {
  const text = formatOrderScheduleLineDisplay(scheduledAt)
  const m = text.match(/^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})/i)
  if (!m?.[1] || !m[2] || !m[3]) return null
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const mi = months.findIndex((x) => x.toLowerCase() === m[1]!.slice(0, 3).toLowerCase())
  if (mi < 0) return null
  const d = new Date(parseInt(m[3]!, 10), mi, parseInt(m[2]!, 10))
  return Number.isNaN(d.getTime()) ? null : d
}

export function slotFromScheduleDisplay(scheduledAt: string): string | null {
  const text = formatOrderScheduleLineDisplay(scheduledAt)
  const times = [...text.matchAll(/(\d{1,2}):(\d{2})(AM|PM)/gi)]
  if (!times.length || !times[0]?.[1] || !times[0]?.[2]) return null
  const normalized = normalizeGridSlot(`${times[0][1]}${times[0][2]}`)
  for (const row of TIME_SLOT_GRID) {
    for (const cell of row) {
      if (cell && normalizeGridSlot(cell) === normalized) return cell
    }
  }
  return null
}

export function formatClockLabelPadded(clock: string): string {
  const m = clock.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return clock.trim()
  return `${m[1].padStart(2, '0')}:${m[2]} ${m[3].toUpperCase()}`
}
